import React, { useState, useEffect, useCallback } from 'react';
import * as Icons from 'lucide-react';

const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8092' : `${window.location.protocol}//${window.location.hostname}:8092`;

const WAChatAdmin = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [conversations, setConversations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const [convRes, deptRes, agentRes] = await Promise.all([
        fetch(`${API_BASE}/api/wa/admin/conversations?limit=50`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/wa/admin/departments`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/wa/admin/agents`, { headers }).catch(() => null),
      ]);
      if (convRes?.ok) { const d = await convRes.json(); setConversations(d.conversations || []); }
      if (deptRes?.ok) { const d = await deptRes.json(); setDepartments(d.departments || []); }
      if (agentRes?.ok) { const d = await agentRes.json(); setAgents(d.agents || []); }
    } catch (err) {
      console.error('Failed to fetch chat data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const statusColors = {
    open: { bg: '#dbeafe', text: '#1e40af', dot: '#3b82f6' },
    active: { bg: '#dcfce7', text: '#166534', dot: '#22c55e' },
    resolved: { bg: '#f3f4f6', text: '#374151', dot: '#9ca3af' },
    escalated: { bg: '#fee2e2', text: '#991b1b', dot: '#ef4444' },
    expired: { bg: '#f3f4f6', text: '#6b7280', dot: '#9ca3af' },
    auto_closed: { bg: '#f3e8ff', text: '#6b21a8', dot: '#a855f7' },
    online: { bg: '#dcfce7', text: '#166534', dot: '#22c55e' },
    away: { bg: '#fef3c7', text: '#92400e', dot: '#f59e0b' },
    offline: { bg: '#f3f4f6', text: '#374151', dot: '#9ca3af' },
    busy: { bg: '#fee2e2', text: '#991b1b', dot: '#ef4444' },
  };

  const getIcon = (name, size = 18) => {
    const Icon = Icons[name];
    return Icon ? <Icon style={{ width: size, height: size }} /> : null;
  };

  const tabs = [
    { key: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { key: 'conversations', label: 'All Conversations', icon: 'MessageCircle' },
    { key: 'departments', label: 'Departments', icon: 'Building2' },
    { key: 'agents', label: 'All Agents', icon: 'Users' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
            <Icons.Headphones className="w-5 h-5 text-white" />
          </div>
          Chat Management (Admin)
        </h1>
        <p className="text-sm text-gray-500 mt-1">Monitor all WhatsApp conversations, agents, and departments across all accounts</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        {[
          { label: 'Total Conversations', value: conversations.length || '0', icon: 'MessageCircle', color: '#25D366' },
          { label: 'Active', value: conversations.filter(c => c.status === 'active' || c.status === 'open').length || '0', icon: 'Zap', color: '#128C7E' },
          { label: 'Escalated', value: conversations.filter(c => c.status === 'escalated').length || '0', icon: 'AlertTriangle', color: '#ef4444' },
          { label: 'Online Agents', value: agents.filter(a => a.status === 'online').length || '0', icon: 'Users', color: '#34B7F1' },
          { label: 'Departments', value: departments.length || '0', icon: 'Building2', color: '#075E54' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${stat.color}14`, color: stat.color }}>
                {getIcon(stat.icon, 18)}
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-[11px] text-gray-500">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="flex border-b border-gray-100 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-2 px-5 py-3.5 text-sm font-medium transition-all border-b-2 whitespace-nowrap"
              style={{
                borderColor: activeTab === tab.key ? '#25D366' : 'transparent',
                color: activeTab === tab.key ? '#25D366' : '#6b7280',
                background: activeTab === tab.key ? 'rgba(37, 211, 102, 0.04)' : 'transparent',
              }}
            >
              {getIcon(tab.icon, 16)}
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <Icons.Loader2 className="w-8 h-8 animate-spin mx-auto text-green-500 mb-3" />
              <p className="text-sm text-gray-500">Loading chat data...</p>
            </div>
          ) : (
            <>
              {/* Overview */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Active Conversations by Status */}
                    <div className="bg-gray-50 rounded-xl p-5">
                      <h3 className="font-semibold text-gray-900 mb-4">Conversations by Status</h3>
                      {conversations.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">No conversation data available yet.</p>
                      ) : (
                        <div className="space-y-3">
                          {['open', 'active', 'escalated', 'resolved', 'expired'].map((status) => {
                            const count = conversations.filter(c => c.status === status).length;
                            const pct = conversations.length > 0 ? (count / conversations.length * 100) : 0;
                            return (
                              <div key={status}>
                                <div className="flex justify-between text-xs mb-1">
                                  <span className="text-gray-600 font-medium capitalize">{status.replace('_', ' ')}</span>
                                  <span className="text-gray-500">{count} ({pct.toFixed(1)}%)</span>
                                </div>
                                <div className="h-2 bg-white rounded-full overflow-hidden border border-gray-100">
                                  <div className="h-full rounded-full" style={{ width: `${pct}%`, background: statusColors[status]?.dot || '#9ca3af' }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Agent Performance */}
                    <div className="bg-gray-50 rounded-xl p-5">
                      <h3 className="font-semibold text-gray-900 mb-4">Agent Status Overview</h3>
                      {agents.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">No agents configured yet.</p>
                      ) : (
                        <div className="space-y-2">
                          {agents.slice(0, 8).map((agent) => (
                            <div key={agent.id} className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-gray-100">
                              <div className="flex items-center gap-3">
                                <div className="relative">
                                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                                    {(agent.name || 'A').charAt(0)}
                                  </div>
                                  <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white" style={{ background: statusColors[agent.status]?.dot || '#9ca3af' }} />
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-gray-900">{agent.name}</p>
                                  <p className="text-[10px] text-gray-400">{agent.department_id ? `Dept #${agent.department_id}` : 'Unassigned'}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-[10px] text-gray-500">{agent.max_concurrent_chats || 5} max</span>
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{
                                  background: statusColors[agent.status]?.bg || '#f3f4f6',
                                  color: statusColors[agent.status]?.text || '#374151',
                                }}>
                                  {agent.status || 'offline'}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* All Conversations */}
              {activeTab === 'conversations' && (
                <div>
                  {conversations.length === 0 ? (
                    <div className="text-center py-12">
                      <Icons.MessageCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">No Conversations</h3>
                      <p className="text-sm text-gray-500">Conversations will appear here when users start chatting via WhatsApp.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-xs text-gray-500 border-b border-gray-100">
                            <th className="text-left px-4 py-3 font-medium">Contact</th>
                            <th className="text-left px-4 py-3 font-medium">WABA</th>
                            <th className="text-left px-4 py-3 font-medium">Category</th>
                            <th className="text-left px-4 py-3 font-medium">Origin</th>
                            <th className="text-center px-4 py-3 font-medium">Messages</th>
                            <th className="text-left px-4 py-3 font-medium">Agent</th>
                            <th className="text-left px-4 py-3 font-medium">Status</th>
                            <th className="text-left px-4 py-3 font-medium">Last Activity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {conversations.map((conv) => (
                            <tr key={conv.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                              <td className="px-4 py-3">
                                <p className="text-sm font-medium text-gray-900">Contact #{conv.contact_id}</p>
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-500">#{conv.waba_account_id}</td>
                              <td className="px-4 py-3">
                                <span className="px-2 py-0.5 rounded-md text-[10px] font-medium bg-gray-100 text-gray-600 capitalize">{conv.category}</span>
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-600 capitalize">{(conv.origin_type || '').replace('_', ' ')}</td>
                              <td className="px-4 py-3 text-center text-sm text-gray-700">{conv.total_messages || 0}</td>
                              <td className="px-4 py-3 text-xs text-gray-600">{conv.assigned_agent_id ? `Agent #${conv.assigned_agent_id}` : 'Unassigned'}</td>
                              <td className="px-4 py-3">
                                <span className="px-2 py-0.5 rounded-md text-xs font-medium" style={{
                                  background: statusColors[conv.status]?.bg || '#f3f4f6',
                                  color: statusColors[conv.status]?.text || '#374151',
                                }}>
                                  {(conv.status || 'unknown').replace('_', ' ')}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-500">{conv.last_message_at || conv.created_at || 'N/A'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Departments */}
              {activeTab === 'departments' && (
                <div className="space-y-4">
                  <div className="flex justify-end">
                    <button className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                      <Icons.Plus className="w-4 h-4" />
                      Add Department
                    </button>
                  </div>
                  {departments.length === 0 ? (
                    <div className="text-center py-12">
                      <Icons.Building2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">No Departments</h3>
                      <p className="text-sm text-gray-500">Create departments to organize chat routing and agent assignment.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {departments.map((dept) => (
                        <div key={dept.id} className="p-5 rounded-xl border border-gray-100 hover:border-green-200 hover:shadow-sm transition-all">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${dept.color || '#25D366'}14`, color: dept.color || '#25D366' }}>
                                <Icons.Building2 className="w-5 h-5" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-gray-900">{dept.name}</h3>
                                <p className="text-[10px] text-gray-400">WABA #{dept.waba_account_id}</p>
                              </div>
                            </div>
                            <button className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
                              <Icons.MoreVertical className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="p-2 rounded-lg bg-gray-50 text-center">
                              <p className="text-sm font-bold text-gray-900">{dept.max_concurrent_chats || 10}</p>
                              <p className="text-[10px] text-gray-500">Max Chats</p>
                            </div>
                            <div className="p-2 rounded-lg bg-gray-50 text-center">
                              <p className="text-sm font-bold text-gray-900">{dept.auto_assign ? 'Yes' : 'No'}</p>
                              <p className="text-[10px] text-gray-500">Auto Assign</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* All Agents */}
              {activeTab === 'agents' && (
                <div className="space-y-4">
                  <div className="flex justify-end">
                    <button className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                      <Icons.UserPlus className="w-4 h-4" />
                      Add Agent
                    </button>
                  </div>
                  {agents.length === 0 ? (
                    <div className="text-center py-12">
                      <Icons.Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">No Agents</h3>
                      <p className="text-sm text-gray-500">Add agents to handle WhatsApp conversations.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {agents.map((agent) => (
                        <div key={agent.id} className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:border-green-200 transition-all">
                          <div className="flex items-center gap-4">
                            <div className="relative">
                              <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                                {(agent.name || 'A').charAt(0)}
                              </div>
                              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white" style={{ background: statusColors[agent.status]?.dot || '#9ca3af' }} />
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900 text-sm">{agent.name}</h3>
                              <p className="text-xs text-gray-400">{agent.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-6">
                            <span className="text-xs text-gray-500 px-2 py-1 rounded-lg bg-gray-50">
                              WABA #{agent.waba_account_id}
                            </span>
                            <span className="text-xs text-gray-500 px-2 py-1 rounded-lg bg-gray-50 capitalize">
                              {agent.role || 'agent'}
                            </span>
                            <div className="text-center">
                              <p className="text-sm font-bold text-gray-900">{agent.total_resolved || 0}</p>
                              <p className="text-[10px] text-gray-400">Resolved</p>
                            </div>
                            {agent.avg_rating > 0 && (
                              <div className="flex items-center gap-1">
                                <Icons.Star className="w-3.5 h-3.5" style={{ color: '#F59E0B', fill: '#F59E0B' }} />
                                <span className="text-sm font-medium text-gray-700">{agent.avg_rating}</span>
                              </div>
                            )}
                            <span className="px-2 py-0.5 rounded-md text-xs font-medium" style={{
                              background: statusColors[agent.status]?.bg || '#f3f4f6',
                              color: statusColors[agent.status]?.text || '#374151',
                            }}>
                              {agent.status || 'offline'}
                            </span>
                            <button className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
                              <Icons.MoreVertical className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WAChatAdmin;
